<template>
  <!-- Show when user has no paid subscription AND (has active trial OR has no access) -->
  <div
    v-if="!hasActiveSubscription && (trialDaysRemaining !== null || !hasAccess)"
    class="relative overflow-hidden rounded-xl shadow-lg"
    :class="isUrgent
      ? 'bg-gradient-to-br from-amber-900 via-orange-800 to-amber-700'
      : 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-800'"
  >
    <!-- Decorative blobs -->
    <div
      class="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-20 pointer-events-none"
      :class="isUrgent ? 'bg-orange-300' : 'bg-blue-400'"
    ></div>
    <div
      class="absolute -bottom-8 left-8 w-28 h-28 rounded-full blur-3xl opacity-15 pointer-events-none"
      :class="isUrgent ? 'bg-yellow-300' : 'bg-indigo-400'"
    ></div>

    <!-- Dot grid overlay -->
    <div
      class="absolute inset-0 pointer-events-none"
      style="background-image: radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px); background-size: 24px 24px; opacity: 0.6;"
    ></div>

    <!-- Shine line -->
    <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-20 pointer-events-none"></div>

    <div class="relative px-6 py-5">

      <!-- T026: Active trial state — show days remaining + "Subscribe now" CTA -->
      <template v-if="trialDaysRemaining !== null">
        <div class="tw-flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span
              class="tw-inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-3"
              :class="isUrgent ? 'bg-orange-400/30 text-orange-200' : 'bg-white/10 text-white/75'"
            >
              <!-- Urgent: warning icon -->
              <svg v-if="isUrgent" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clip-rule="evenodd"/>
              </svg>
              <!-- Normal: clock icon -->
              <svg v-else class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clip-rule="evenodd"/>
              </svg>
              {{ isUrgent ? $t('trp_banner_trial_expiring') : $t('trp_banner_trial_active') }}
            </span>
            <h3 class="text-white font-bold text-lg m-0 mb-1 leading-tight">{{ $t('trp_banner_title') }}</h3>
            <p
              class="text-sm m-0"
              :class="isUrgent ? 'text-orange-200' : 'text-white/60'"
            >
              <template v-if="isUrgent">
                {{ $t('trp_banner_expires_in') }} <strong :class="isUrgent ? 'text-orange-100' : 'text-white/90'">{{ trialDaysRemaining }} {{ trialDaysRemaining === 1 ? $t('trp_banner_day') : $t('trp_banner_days') }}</strong>
                {{ $t('trp_banner_keep_access') }}
              </template>
              <template v-else>
                {{ $t('trp_banner_trial_expires_in') }} <strong class="text-white/90">{{ trialDaysRemaining }} {{ trialDaysRemaining === 1 ? $t('trp_banner_day') : $t('trp_banner_days') }}</strong>.
              </template>
            </p>
          </div>

          <!-- T029: Subscribe now button -->
          <button
            @click="subscribeNow"
            :disabled="subscribingFromTrial"
            class="shrink-0 tw-inline-flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-lg shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            :class="isUrgent
              ? 'bg-white text-amber-900 hover:shadow-lg hover:scale-105'
              : 'bg-white text-slate-900 hover:shadow-lg hover:scale-105'"
          >
            <template v-if="subscribingFromTrial">
              <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 0 12 12h-4z"></path>
              </svg>
              {{ $t('trp_banner_redirecting') }}
            </template>
            <template v-else>
              {{ $t('trp_subscribe_now') }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </template>
          </button>
        </div>

        <div v-if="subscribeError" class="mt-3 rounded-lg border px-3 py-2 text-sm bg-red-900/40 border-red-400/30 text-red-200">
          {{ subscribeError }}
        </div>
      </template>

      <!-- T024: Post-expiry / no trial available state — "Subscribe" CTA -->
      <template v-else-if="!trialAvailable">
        <div class="tw-flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span class="tw-inline-flex items-center gap-1.5 bg-white/10 text-white/75 text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-3">
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              {{ $t('trp_banner_premium') }}
            </span>
            <h3 class="text-white font-bold text-lg m-0 mb-1 leading-tight">{{ $t('trp_banner_title') }}</h3>
            <p class="text-white/60 text-sm m-0">
              {{ $t('trp_entry_pulse_title') }} &nbsp;·&nbsp; {{ $t('trp_entry_radar_title') }} &nbsp;·&nbsp; {{ $t('trp_entry_deepdive_title') }}
            </p>
          </div>
          <router-link
            to="/transition-reports/trial"
            class="shrink-0 tw-inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-5 py-2.5 rounded-lg no-underline shadow-md hover:shadow-lg hover:scale-105 transition-all"
          >
            {{ $t('trp_banner_subscribe') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
        </div>
      </template>

      <!-- Trial available state — "Claim free trial" CTA -->
      <template v-else>
        <div class="tw-flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span class="tw-flex w-fit items-center gap-1.5 bg-emerald-400/25 text-emerald-200 text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-3">
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                      d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"
                      clip-rule="evenodd"/>
              </svg>
              {{ $t('trp_banner_two_months_free') }}
            </span>
            <h3 class="text-white font-bold text-lg m-0 mb-1 leading-tight">{{ $t('trp_banner_title') }}</h3>
            <p class="text-white/60 text-sm m-0">
              {{ $t('trp_entry_pulse_title') }} &nbsp;·&nbsp; {{ $t('trp_entry_radar_title') }} &nbsp;·&nbsp; {{ $t('trp_entry_deepdive_title') }}
            </p>
          </div>
          <router-link
            to="/transition-reports/trial"
            class="shrink-0 tw-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-5 py-2.5 rounded-lg no-underline shadow-md hover:shadow-lg hover:scale-105 transition-all"
          >
            {{ $t('trp_banner_claim_trial') }}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
  import { subscribeFromTrial } from '@/api/transitionReportsSubscription'

  export default {
    name: 'TransitionReportsTrialBanner',

    data () {
      return {
        subscribingFromTrial: false,
        subscribeError: null,
      }
    },

    computed: {
      hasAccess () {
        return this.$store.getters.hasTransitionReportAccess
      },
      trialAvailable () {
        return this.$store.getters.isTransitionReportTrialAvailable
      },
      trialDaysRemaining () {
        return this.$store.getters.transitionReportTrialDaysRemaining
      },
      hasActiveSubscription () {
        return this.$store.getters.hasActiveTransitionReportSubscription
      },
      isUrgent () {
        return this.trialDaysRemaining !== null && this.trialDaysRemaining <= 30
      },
    },

    methods: {
      async subscribeNow () {
        this.subscribingFromTrial = true
        this.subscribeError = null

        try {
          const result = await subscribeFromTrial()

          if (!result.fallback && result.checkout_url) {
            window.location.href = result.checkout_url
          } else {
            // Fallback: prefill data available, navigate to checkout form
            const prefill = result.prefill || {}
            const query = {}
            Object.keys(prefill).forEach(key => {
              if (prefill[key] !== null && prefill[key] !== undefined) {
                query[key] = String(prefill[key])
              }
            })
            this.$router.push({ path: '/transition-reports/trial', query })
          }
        } catch {
          this.subscribeError = this.$t('trp_banner_subscribe_error')
          this.subscribingFromTrial = false
        }
      },
    },
  }
</script>
